/**
 * Billing Page
 *
 * Dashboard for viewing tenant quotas and invoices.
 */

import React, { useState, useEffect } from "react";
import { ArrowDownTrayIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { billingService } from "../api/billing.service";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { useLayoutProps } from "../hooks/useLayoutProps";
import type {
  QuotasResponse,
  Invoice,
  InvoicePaymentStatus,
  QuotaMetric,
} from "../types/billing.types";
import {
  invoicePaymentStatusLabels,
  invoicePaymentStatusColors,
  planLabels,
  planColors,
} from "../types/billing.types";
import { useToast } from "../hooks/useToast";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const BillingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"quotas" | "invoices">("quotas");
  const layoutProps = useLayoutProps();

  return (
    <DashboardLayout {...layoutProps}>
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>Billing</h1>
        <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>View your plan quotas and invoices</p>
      </div>
      {/* Tabs */}
      <div className="border-b mb-6" style={{ borderColor: 'var(--color-border)' }}>
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("quotas")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "quotas"
                ? "border-primary-500 text-primary-600"
                : "border-transparent hover:border-gray-300"
            }`}
            style={activeTab !== "quotas" ? { color: 'var(--color-text-muted)' } : undefined}
          >
            Quotas & Usage
          </button>
          <button
            onClick={() => setActiveTab("invoices")}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === "invoices"
                ? "border-primary-500 text-primary-600"
                : "border-transparent hover:border-gray-300"
            }`}
            style={activeTab !== "invoices" ? { color: 'var(--color-text-muted)' } : undefined}
          >
            Invoices
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === "quotas" ? <QuotasTab /> : <InvoicesTab />}
    </DashboardLayout>
  );
};

// ============================================================================
// QUOTAS TAB
// ============================================================================

const QuotasTab: React.FC = () => {
  const [quotas, setQuotas] = useState<QuotasResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    loadQuotas();
  }, []);

  const loadQuotas = async () => {
    try {
      const data = await billingService.getQuotas();
      setQuotas(data);
    } catch (error) {
      toast.error("Failed to load quotas");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!quotas) {
    return (
      <div className="text-center py-12" style={{ color: 'var(--color-text-muted)' }}>
        Unable to load quota information
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Plan Info */}
      <div className="card shadow-card rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium" style={{ color: 'var(--color-text-primary)' }}>Current Plan</h3>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Your subscription plan and features
            </p>
          </div>
          <span
            className={`px-4 py-2 text-sm font-semibold rounded-full ${
              planColors[quotas.plan] || "bg-gray-100 text-gray-800"
            }`}
          >
            {planLabels[quotas.plan] || quotas.plan}
          </span>
        </div>

        {/* Features */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(quotas.features).map(([feature, enabled]) => (
            <div
              key={feature}
              className={`px-3 py-2 rounded-lg text-sm ${
                enabled ? "bg-accent-50 text-accent-700" : ""
              }`}
              style={!enabled ? { backgroundColor: 'var(--color-bg-sunken)', color: 'var(--color-text-muted)' } : undefined}
            >
              <span className="mr-2">{enabled ? "+" : "x"}</span>
              {formatFeatureName(feature)}
            </div>
          ))}
        </div>
      </div>

      {/* Quota Metrics */}
      <div className="card shadow-card rounded-lg">
        <div className="px-6 py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <h3 className="text-lg font-medium" style={{ color: 'var(--color-text-primary)' }}>Resource Usage</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quotas.quotaMetrics.map((metric) => (
            <QuotaCard key={metric.name} metric={metric} />
          ))}
        </div>
        {quotas.usage?.lastUpdated && (
          <div className="px-6 pb-4 text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Last updated: {new Date(quotas.usage.lastUpdated).toLocaleString()}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// QUOTA CARD COMPONENT
// ============================================================================

interface QuotaCardProps {
  metric: QuotaMetric;
}

const QuotaCard: React.FC<QuotaCardProps> = ({ metric }) => {
  const getProgressColor = () => {
    if (metric.warning) return "bg-yellow-500";
    if (metric.percentage >= 100) return "bg-red-500";
    return "bg-primary-500";
  };

  return (
    <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-sunken)' }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>{metric.label}</span>
        {metric.warning && (
          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
        )}
      </div>
      <div className="text-2xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
        {metric.displayValue}
      </div>
      <div className="w-full rounded-full h-2" style={{ backgroundColor: 'var(--color-border)' }}>
        <div
          className={`h-2 rounded-full transition-all ${getProgressColor()}`}
          style={{ width: `${Math.min(metric.percentage, 100)}%` }}
        />
      </div>
      <div className="mt-1 text-xs text-right" style={{ color: 'var(--color-text-muted)' }}>
        {metric.percentage}% used
      </div>
    </div>
  );
};

// ============================================================================
// INVOICES TAB
// ============================================================================

const InvoicesTab: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [paymentFilter, setPaymentFilter] = useState<InvoicePaymentStatus | "">("");
  const toast = useToast();

  useEffect(() => {
    loadInvoices();
  }, [paymentFilter]);

  const loadInvoices = async () => {
    setIsLoading(true);
    try {
      const data = await billingService.listInvoices({
        paymentStatus: paymentFilter || undefined,
      });
      setInvoices(data.items);
    } catch (error) {
      toast.error("Failed to load invoices");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (invoice: Invoice) => {
    try {
      const { downloadUrl } = await billingService.getDownloadUrl(invoice.invoiceId);
      window.open(downloadUrl, "_blank");
    } catch (error) {
      toast.error("Failed to get download URL");
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    const value = amount / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex justify-end">
        <select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value as InvoicePaymentStatus | "")}
          className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <option value="">All Payments</option>
          <option value="pending">Pending</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="partial">Partial</option>
        </select>
      </div>

      {/* Invoices Table */}
      <div className="card shadow-card rounded-lg overflow-hidden">
        <table className="min-w-full divide-y" style={{ borderColor: 'var(--color-border)' }}>
          <thead style={{ backgroundColor: 'var(--color-bg-sunken)' }}>
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Invoice #
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Due Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Total
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }}>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center" style={{ color: 'var(--color-text-muted)' }}>
                  No invoices found
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.invoiceId} className="hover:bg-surface-sunken">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      {invoice.invoiceNumber}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    {formatDate(invoice.invoiceDate)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    {formatDate(invoice.dueDate)}
                  </td>
                  <td className="px-6 py-4 text-sm max-w-xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                    {invoice.description}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                    {formatCurrency(invoice.totalAmount, invoice.currency)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        invoicePaymentStatusColors[invoice.paymentStatus]
                      }`}
                    >
                      {invoicePaymentStatusLabels[invoice.paymentStatus]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => handleDownload(invoice)}
                      className="p-2 rounded-lg hover:text-primary-600 hover:bg-surface-sunken"
                      style={{ color: 'var(--color-text-muted)' }}
                      title="Download PDF"
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// HELPERS
// ============================================================================

function formatFeatureName(feature: string): string {
  return feature
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

export default BillingPage;
